import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CodeIcon from '@mui/icons-material/Code';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMysqlInfo,
  fetchMysqlDatabases,
  fetchMysqlCharsets,
  createDatabase,
  dropDatabase,
  fetchDumpToken,
  triggerDumpDownload,
  type MySQLDatabase,
  type TableColumn,
} from '../api/mysql';
import { useTables, useTableSchema } from '../hooks/useMySQLSchema';
import { useTableInfo } from '../hooks/useMySQLData';
import { DbTreeSidebar } from '../components/mysql/DbTreeSidebar';
import { DataBrowserTab } from '../components/mysql/DataBrowserTab';
import { QueryEditorTab } from '../components/mysql/QueryEditorTab';
import { MySQLImportDialog } from '../components/mysql/MySQLImportDialog';
import { usePermission } from '../hooks/usePermission';
import { useSnackbar } from '../context/SnackbarContext';

// ── helpers ────────────────────────────────────────────────────────────────

function fmtBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function fmtUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}

// ── Info bar ───────────────────────────────────────────────────────────────

function InfoBar() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['mysql', 'info'],
    queryFn: fetchMysqlInfo,
    refetchInterval: 30_000,
    retry: false,
  });

  return (
    <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {isLoading ? (
            <CircularProgress size={14} />
          ) : error ? (
            <><ErrorIcon color="error" fontSize="small" /><Typography variant="body2">Unavailable</Typography></>
          ) : (
            <><CheckCircleIcon color="success" fontSize="small" /><Typography variant="body2">Connected</Typography></>
          )}
        </Stack>
        <Box><Typography variant="caption" color="text.secondary">Version</Typography>
          <Typography variant="body2" ml={0.5} component="span">{isLoading ? '…' : (data?.version ?? '—')}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Uptime</Typography>
          <Typography variant="body2" ml={0.5} component="span">{isLoading ? '…' : (data ? fmtUptime(data.uptime_seconds) : '—')}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Connections</Typography>
          <Typography variant="body2" ml={0.5} component="span">{isLoading ? '…' : (data?.threads_connected ?? '—')}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Total size</Typography>
          <Typography variant="body2" ml={0.5} component="span">{isLoading ? '…' : (data ? fmtBytes(data.total_size_bytes) : '—')}</Typography></Box>
      </Stack>
    </Box>
  );
}

// ── Create database dialog ─────────────────────────────────────────────────

interface CreateDialogProps { open: boolean; onClose: () => void; }

function CreateDatabaseDialog({ open, onClose }: CreateDialogProps) {
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();
  const [name, setName] = useState('');
  const [charset, setCharset] = useState('utf8mb4');
  const [collation, setCollation] = useState('');

  const { data: charsetData } = useQuery({
    queryKey: ['mysql', 'charsets'],
    queryFn: fetchMysqlCharsets,
    enabled: open,
  });

  const collations = charsetData?.charsets[charset] ?? [];

  const mutation = useMutation({
    mutationFn: () => createDatabase({ name, charset, collation }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mysql'] });
      showSnackbar(`Database "${name}" created`, 'success');
      handleClose();
    },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  function handleClose() { setName(''); setCharset('utf8mb4'); setCollation(''); onClose(); }
  const valid = /^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/.test(name) && charset !== '' && collation !== '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create database</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Database name" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth
            helperText="Letters, digits, underscores. Must start with a letter or underscore." />
          <FormControl size="small" fullWidth>
            <InputLabel>Character set</InputLabel>
            <Select value={charset} label="Character set" onChange={(e) => { setCharset(e.target.value); setCollation(''); }}>
              {['utf8mb4', 'utf8', 'latin1', 'ascii'].map((cs) => <MenuItem key={cs} value={cs}>{cs}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Collation</InputLabel>
            <Select value={collation} label="Collation" onChange={(e) => setCollation(e.target.value)} disabled={collations.length === 0}>
              {collations.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Drop database dialog ───────────────────────────────────────────────────

interface DropDialogProps { database: MySQLDatabase | null; onClose: () => void; }

function DropDatabaseDialog({ database, onClose }: DropDialogProps) {
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();
  const [confirm, setConfirm] = useState('');

  const mutation = useMutation({
    mutationFn: () => dropDatabase(database!.name, confirm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mysql'] });
      showSnackbar(`Database "${database!.name}" dropped`, 'success');
      handleClose();
    },
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  });

  function handleClose() { setConfirm(''); onClose(); }

  return (
    <Dialog open={database !== null} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Drop database</DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          This permanently deletes <strong>{database?.name}</strong> and all its tables. This action cannot be undone.
        </Alert>
        <TextField label={`Type "${database?.name}" to confirm`} value={confirm} onChange={(e) => setConfirm(e.target.value)} size="small" fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={confirm !== database?.name || mutation.isPending} onClick={() => mutation.mutate()}>
          Drop database
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Schema tab ─────────────────────────────────────────────────────────────

const KEY_STYLES: Record<string, { label: string; bgcolor: string; color: string }> = {
  PRI: { label: '🔑 PRI', bgcolor: 'rgba(229,57,53,0.15)', color: '#ef9a9a' },
  UNI: { label: '◆ UNI', bgcolor: 'rgba(41,182,246,0.12)', color: '#81d4fa' },
  MUL: { label: '⊕ MUL', bgcolor: 'rgba(255,152,0,0.12)', color: '#ffb74d' },
};

const ALL_COLS = [
  { id: 'name' as const,          label: 'Column'     },
  { id: 'type' as const,          label: 'Type'       },
  { id: 'max_length' as const,    label: 'Max Length' },
  { id: 'nullable' as const,      label: 'Nullable'   },
  { id: 'default_value' as const, label: 'Default'    },
  { id: 'key' as const,           label: 'Key'        },
  { id: 'extra' as const,         label: 'Extra'      },
];
type ColId = (typeof ALL_COLS)[number]['id'];

function SchemaTab({ db, table }: { db: string | null; table: string | null }) {
  const [visibleCols, setVisibleCols] = useState<ColId[]>(['name', 'type', 'max_length', 'nullable', 'key', 'extra']);
  const { data: columns, isLoading } = useTableSchema(db, table);
  const { data: info } = useTableInfo(db, table);

  if (!db || !table) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <Typography color="text.secondary">Select a table from the sidebar.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {info && (
        <Stack direction="row" spacing={3} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box><Typography variant="caption" color="text.secondary">Rows</Typography>
            <Typography variant="body2" fontFamily="monospace">~{info.rowCount.toLocaleString()}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Engine</Typography>
            <Typography variant="body2">{info.engine}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Charset</Typography>
            <Typography variant="body2" fontFamily="monospace">{info.charset}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">Size</Typography>
            <Typography variant="body2">{fmtBytes(info.sizeBytes)}</Typography></Box>
        </Stack>
      )}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 2, py: 0.5, flexShrink: 0 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Columns</InputLabel>
          <Select multiple value={visibleCols} label="Columns"
            renderValue={(s) => `${s.length} of ${ALL_COLS.length} columns`}
            onChange={(e) => setVisibleCols(e.target.value as ColId[])}>
            {ALL_COLS.map((col) => (
              <MenuItem key={col.id} value={col.id}>
                <Checkbox size="small" checked={visibleCols.includes(col.id)} disableRipple />
                <ListItemText primary={col.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
        {columns && (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {ALL_COLS.filter((c) => visibleCols.includes(c.id)).map((c) => (
                    <TableCell key={c.id} sx={{ whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {columns.map((col: TableColumn) => (
                  <TableRow key={col.name} hover>
                    {visibleCols.includes('name') && (
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{col.name}</TableCell>
                    )}
                    {visibleCols.includes('type') && (
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'info.main' }}>{col.type}</TableCell>
                    )}
                    {visibleCols.includes('max_length') && <TableCell>{col.max_length ?? '—'}</TableCell>}
                    {visibleCols.includes('nullable') && <TableCell>{col.nullable ? 'YES' : 'NO'}</TableCell>}
                    {visibleCols.includes('default_value') && (
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {col.default_value ?? <em style={{ color: '#aaa' }}>NULL</em>}
                      </TableCell>
                    )}
                    {visibleCols.includes('key') && (
                      <TableCell>
                        {col.key && KEY_STYLES[col.key] ? (
                          <Chip
                            label={KEY_STYLES[col.key].label}
                            size="small"
                            sx={{ bgcolor: KEY_STYLES[col.key].bgcolor, color: KEY_STYLES[col.key].color, fontSize: '0.7rem' }}
                          />
                        ) : col.key ? (
                          <Chip label={col.key} size="small" />
                        ) : '—'}
                      </TableCell>
                    )}
                    {visibleCols.includes('extra') && (
                      <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{col.extra || '—'}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

// ── Databases tab ──────────────────────────────────────────────────────────

interface DatabasesTabProps {
  databases: MySQLDatabase[];
  isLoading: boolean;
  isConnectionError: boolean;
  canWrite: boolean;
  canDump: boolean;
  onRefetch: () => void;
  onImport: (db: MySQLDatabase) => void;
  onDrop: (db: MySQLDatabase) => void;
}

function DatabasesTab({ databases, isLoading, isConnectionError, canWrite, canDump, onRefetch, onImport, onDrop }: DatabasesTabProps) {
  const showSnackbar = useSnackbar();
  const [downloadingDb, setDownloadingDb] = useState<string | null>(null);

  async function handleDownload(db: MySQLDatabase) {
    setDownloadingDb(db.name);
    try {
      const { token } = await fetchDumpToken(db.name);
      triggerDumpDownload(db.name, token);
    } catch {
      showSnackbar(`Failed to start dump for "${db.name}"`, 'error');
    } finally {
      setDownloadingDb(null);
    }
  }

  return (
    <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
      {isConnectionError && (
        <Alert severity="error" action={<Button size="small" onClick={onRefetch}>Retry</Button>} sx={{ mb: 2 }}>
          MySQL connection failed. Is the MySQL service running?
        </Alert>
      )}
      {databases.length > 0 && (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Character set</TableCell>
                <TableCell>Collation</TableCell>
                <TableCell>Tables</TableCell>
                <TableCell>Size</TableCell>
                {(canDump || canWrite) && <TableCell align="right" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {databases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(canDump || canWrite) ? 6 : 5} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>No user databases found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                databases.map((db) => (
                  <TableRow key={db.name} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{db.name}</Typography></TableCell>
                    <TableCell><Chip label={db.charset} size="small" variant="outlined" /></TableCell>
                    <TableCell><Typography variant="caption" fontFamily="monospace">{db.collation}</Typography></TableCell>
                    <TableCell>{db.table_count}</TableCell>
                    <TableCell>{fmtBytes(db.size_bytes)}</TableCell>
                    {(canDump || canWrite) && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {canDump && (
                            <Tooltip title="Download dump (.sql.gz)">
                              <span>
                                <IconButton size="small" onClick={() => void handleDownload(db)} disabled={downloadingDb === db.name}>
                                  {downloadingDb === db.name ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {canWrite && (
                            <Tooltip title="Import dump">
                              <IconButton size="small" onClick={() => onImport(db)}><UploadIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                          {canWrite && (
                            <Tooltip title="Drop database">
                              <IconButton size="small" color="error" onClick={() => onDrop(db)}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ── Tables load for sidebar ────────────────────────────────────────────────

function useFirstTable(db: string | null) {
  const { data: tables } = useTables(db);
  return tables?.[0] ?? null;
}
void useFirstTable; // used by parent — keep linter happy

// ── Main page ──────────────────────────────────────────────────────────────

export function MySQL() {
  const canRead = usePermission('mysql.read');
  const canWrite = usePermission('mysql.write');
  const canDump = usePermission('mysql.dump');
  const qc = useQueryClient();

  const [tab, setTab] = useState(0);
  const [selectedDb, setSelectedDb] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [dropTarget, setDropTarget] = useState<MySQLDatabase | null>(null);
  const [importTarget, setImportTarget] = useState<MySQLDatabase | null>(null);

  const { data: charsetData } = useQuery({
    queryKey: ['mysql', 'charsets'],
    queryFn: fetchMysqlCharsets,
    enabled: canRead,
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['mysql', 'databases'],
    queryFn: fetchMysqlDatabases,
    enabled: canRead,
    refetchInterval: 30_000,
    retry: false,
  });

  if (!canRead) {
    return <Alert severity="error">You do not have permission to view MySQL info.</Alert>;
  }

  const isNotConfigured = error instanceof Error && (error as { response?: { status?: number } }).response?.status === 501;
  const isConnectionError = error instanceof Error && !isNotConfigured;
  const databases = data?.databases ?? [];
  const charsets = charsetData?.charsets ?? {};

  if (isNotConfigured) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          MySQL is not configured. Add a <code>mysql:</code> block to <code>/etc/webadmin/app.yaml</code> and restart the service.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="h5" fontWeight={700}>MySQL</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<CodeIcon />} onClick={() => setTab(2)} variant="outlined">
            Query Editor
          </Button>
          {canWrite && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { void qc.invalidateQueries({ queryKey: ['mysql', 'charsets'] }); setCreateOpen(true); }}
            >
              Create DB
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Info bar */}
      <InfoBar />

      {/* 3-panel body */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <DbTreeSidebar
          databases={databases}
          selectedDb={selectedDb}
          selectedTable={selectedTable}
          onSelectTable={(db, table) => { setSelectedDb(db); setSelectedTable(table); }}
        />

        {/* Main panel */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Tab bar */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Tabs value={tab} onChange={(_, v: number) => setTab(v)}>
              <Tab label="Schema" />
              <Tab label="Data" />
              <Tab label="Query Editor" />
              <Tab label="Databases" />
            </Tabs>
          </Box>

          {/* Breadcrumb */}
          {(selectedDb || selectedTable) && (
            <Box sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                {selectedDb ?? ''}
                {selectedTable ? <> <span style={{ opacity: 0.5 }}>›</span> {selectedTable}</> : null}
              </Typography>
            </Box>
          )}

          {/* Tab content */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {tab === 0 && <SchemaTab db={selectedDb} table={selectedTable} />}
            {tab === 1 && <DataBrowserTab db={selectedDb} table={selectedTable} />}
            {tab === 2 && <QueryEditorTab defaultDb={selectedDb} databases={databases} />}
            {tab === 3 && (
              <DatabasesTab
                databases={databases}
                isLoading={isLoading}
                isConnectionError={isConnectionError}
                canWrite={canWrite}
                canDump={canDump}
                onRefetch={() => void refetch()}
                onImport={(db) => setImportTarget(db)}
                onDrop={(db) => setDropTarget(db)}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Divider />

      <CreateDatabaseDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <DropDatabaseDialog database={dropTarget} onClose={() => setDropTarget(null)} />
      <MySQLImportDialog
        open={importTarget !== null}
        db={importTarget}
        charsets={charsets}
        onClose={() => setImportTarget(null)}
      />
    </Box>
  );
}
